import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    // Load profile from Supabase
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .single();

    if (profileError) {
      setError('Failed to load profile.');
      setLoading(false);
      return;
    }

    // Save into global auth store
    setUser({
      id: data.user.id,
      email: data.user.email!,
      role: profile.role,
      adminLevel: profile.admin_level || 0,
    });

    // Redirect based on role
    if (profile.role === 'admin' || profile.role === 'superadmin') {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }

    setLoading(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4">
      <div className="w-full max-w-md">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
            <Link to="/" className="text-2xl font-bold text-nhs-blue">
              TALIS'ON SERVICES
            </Link>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Welcome Back!
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Sign in to your account
            </p>
          </div>

        {error && (
          <div className="bg-red-50 border border-red-200 p-3 rounded text-red-600 mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
          />

          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-nhs-blue focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-nhs-blue text-white py-3 rounded flex justify-center items-center gap-2 rounded-lg"
          >
            {loading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Sign In'}
          </button>
        </form>

        <div className="text-center mt-6 text-sm">
          <Link to="/forgot-password" className="text-nhs-blue">
            Forgot password?
          </Link>
        </div>

        <div className="text-center mt-4 text-sm">
          Don't have an account?{' '}
          <Link to="/register" className="text-nhs-blue">
            Sign up
          </Link>
        </div>
      </div>
    </div>
  </div>
  );
}